import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getLocaleConfig, localeCodes, localizePath, stripLocalePrefix } from "./locales";
import { useI18n } from "./LocaleContext";

function ensureMeta(name: string): HTMLMetaElement {
  let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);

  if (!meta) {
    meta = document.createElement("meta");
    meta.name = name;
    document.head.append(meta);
  }

  return meta;
}

function createManagedLink(rel: string, attributes: Record<string, string>): HTMLLinkElement {
  const link = document.createElement("link");
  link.rel = rel;
  link.dataset.timbroamSeo = "true";

  for (const [name, value] of Object.entries(attributes)) {
    link.setAttribute(name, value);
  }

  document.head.append(link);

  return link;
}

export function LocalizedSeo() {
  const location = useLocation();
  const { locale, config, t } = useI18n();

  useEffect(() => {
    const basePath = stripLocalePrefix(location.pathname);
    const origin = typeof window === "undefined" ? "https://timbroam.pages.dev" : window.location.origin;
    const canonicalUrl = `${origin}${localizePath(locale, basePath)}`;

    document.documentElement.lang = config.htmlLang;
    document.documentElement.dir = config.dir;
    document.title = t.seo.title;
    ensureMeta("description").content = t.seo.description;

    document.querySelectorAll('link[data-timbroam-seo="true"]').forEach((element) => element.remove());
    createManagedLink("canonical", { href: canonicalUrl });

    for (const alternateLocale of localeCodes) {
      const alternateConfig = getLocaleConfig(alternateLocale);
      createManagedLink("alternate", {
        href: `${origin}${localizePath(alternateLocale, basePath)}`,
        hreflang: alternateConfig.htmlLang
      });
    }

    createManagedLink("alternate", {
      href: `${origin}${localizePath("en", basePath)}`,
      hreflang: "x-default"
    });
  }, [config, locale, location.pathname, t]);

  return null;
}
